import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, useSeo } from "../lib/ui";

const QUESTIONS = [
  ["Every county knew what it needed", "What if every county could identify the football facilities it needs — and the qualified people capable of delivering them?"],
  ["Sites entered the light", "What if landowners, schools, clubs and county governments could register potential sites through a transparent preliminary process?"],
  ["Agency work went to agents", "What if every property transaction was referred to an EARB-registered estate agent?"],
  ["Values came from valuers", "What if every formal land value was produced by a registered valuer?"],
  ["Title was read by advocates", "What if every title and contract was reviewed by a Kenyan advocate holding a current practising certificate?"],
  ["Funding partners could look first", "What if prospective funding partners could explore development scenarios before obtaining independent professional advice?"],
  ["Regulated money stayed regulated", "What if regulated investment activity was handled only by appropriately CMA-licensed professionals?"],
  ["Designers were on the register", "What if architects, engineers, planners, surveyors, environmental experts and contractors were verified against official registers?"],
  ["Projects named their people", "What if every project identified its responsible professionals, approvals, funding status, risks and delivery stage?"],
  ["Players found the licensed", "What if players, clubs and academies could find independently verified coaches, medical practitioners and FIFA-licensed football agents?"],
  ["The diaspora added, never displaced", "What if diaspora professionals could contribute expertise while regulated Kenyan work remained with professionals authorised to perform it?"],
  ["The connector stayed a connector", "What if KickStartKenyaFootball connected the ecosystem without replacing regulators, governing bodies or licensed professionals?"],
];

/** The proposition, asked as questions. No claim, no promise, no regulated service. */
export default function WhatIf() {
  useSeo({
    title: "What If Kenyan Football Had A Verified Professional Network? | KickStartKenyaFootball",
    description:
      "Twelve questions about a Kenyan football ecosystem where every regulated question sits with a licensed professional — estate agents, valuers, advocates, surveyors, planners, architects, engineers, contractors and CMA-licensed intermediaries — verified against official registers.",
  });

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="what-if-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">The proposition, asked rather than claimed</p>
          <h1 className="display-1 mt-5 max-w-5xl">
            What If Kenyan Football Had A Verified Professional Network?
          </h1>
          <p className="body-copy mt-7 max-w-3xl">
            None of what follows is a promise, an offer or a service on sale. They are the questions this
            platform exists to keep asking — and the answer to every one of them ends with a person holding a
            Kenyan licence, not with us.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-px bg-zinc-800 md:grid-cols-2 lg:grid-cols-3" data-testid="what-if-list">
          {QUESTIONS.map(([title, question], i) => (
            <article key={title} className="group bg-[#0b0b0b] p-7 transition-colors hover:bg-[#111]"
              data-testid={`what-if-${i}`}>
              <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-ochre">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h2 className="mt-4 font-display text-lg uppercase leading-tight tracking-tight text-white">
                {title}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{question}</p>
            </article>
          ))}
        </div>

        <section className="mt-20 max-w-3xl border-l-2 border-ochre bg-[#0b0b0b] p-8" data-testid="what-if-close">
          <p className="text-base leading-relaxed text-white md:text-lg">
            Explore your county. Identify what it needs. Connect with the people authorised to help build what
            comes next.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/counties" className="btn-primary inline-flex items-center gap-2" data-testid="what-if-counties">
              Explore your county <ArrowRight size={14} />
            </Link>
            <Link to="/professionals" className="btn-ghost" data-testid="what-if-professionals">
              See the verified professional network
            </Link>
            <Link to="/apply" className="btn-ghost" data-testid="what-if-apply">
              Tell us about an opportunity
            </Link>
          </div>
        </section>

        <p className="mono mt-12 max-w-3xl border-l-2 border-zinc-700 bg-[#0b0b0b] p-6 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400"
          data-testid="what-if-position">
          KickStartKenyaFootball performs no work requiring a Kenyan licence, registration or practising
          certificate that it does not hold, and any registration held is published on our governance page with
          its number and the date it was granted. Every regulated activity is referred to an independent
          professional holding the required Kenyan authorisation. No funding, transaction or payment is promised
          anywhere on this site.
        </p>
      </div>
    </div>
  );
}
